import React, { useContext } from "react";
import { AnchorButton, Button } from "@blueprintjs/core";
import { faCaretDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Menu, Stack } from "@mantine/core";

import { PolicyMenu } from "@components/PolicyMenu";
import { useOrg } from "@hooks/useOrg";
import { usePolicies } from "@hooks/usePolicies";
import { useUser } from "@hooks/useUser";
import { PermissionsContext } from "@providers/PermissionsProvider";
import { Permission } from "@types";
import { Feature } from "@utils/feature";

import { PolicyRule } from "@pages/policy/types";

interface Props {
  toAdd: string[];
  minWidth?: string;
  onSelect: (slug: string) => Promise<PolicyRule[]>;
  minimal?: boolean;
  buttonText?: string;
  Target?: any;
  showOpenInAppCTA?: boolean;
}

// careful, this is a smart component that will trigger an API call
export const AddToPolicyButton: React.FC<Props> = ({
  onSelect,
  toAdd,
  minWidth,
  minimal = false,
  buttonText = "Add to Policy",
  Target,
  showOpenInAppCTA = false,
}) => {
  const org = useOrg();
  const user = useUser();
  const { permissions } = useContext(PermissionsContext);

  const canAccessPolicies = permissions.includes(Permission.panel_policy);
  const isNolicy = Feature.isNolicy({ org });
  const nolicyInclusiveText = isNolicy
    ? buttonText.replace("Policy", "Rule Board")
    : buttonText;

  const { data: policiesData, refetch: refreshPolicies } = usePolicies();

  const isPolicyButtonDisabled = user && org && !canAccessPolicies;

  return (
    <Menu withArrow position="bottom" width={240}>
      <Menu.Target>
        {Target ? (
          <div>
            <Target onClick={() => refreshPolicies()}>
              {user === undefined ? "Use in CI" : nolicyInclusiveText}&nbsp;
              <FontAwesomeIcon icon={faCaretDown} />
            </Target>
          </div>
        ) : (
          <div>
            <Button
              style={{
                maxWidth: "300px",
                whiteSpace: "nowrap",
                minWidth: minWidth && "300px",
                borderRadius: "3px",
              }}
              intent="primary"
              onClick={() => refreshPolicies()}
              minimal={minimal}
              disabled={isPolicyButtonDisabled}
            >
              {user === undefined ? "Use in CI" : nolicyInclusiveText}&nbsp;
              <FontAwesomeIcon icon={faCaretDown} />
            </Button>
          </div>
        )}
      </Menu.Target>
      {!isPolicyButtonDisabled && (
        <Menu.Dropdown>
          <PolicyMenu
            policies={policiesData?.policies ?? []}
            toAdd={toAdd}
            onSelect={onSelect}
          />
          {showOpenInAppCTA && (
            <Stack p="sm" sx={{ color: "black", whiteSpace: "break-spaces" }}>
              Want to see your findings? Open in the App!
              <AnchorButton intent="primary" href="/orgs/-/findings">
                See in App
              </AnchorButton>
            </Stack>
          )}
        </Menu.Dropdown>
      )}
    </Menu>
  );
};
